import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Phone, Mail, Trash2 } from "lucide-react";
import { AdminHeader } from "@/pages/admin/AdminLayout";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const STATUSES = ["new", "contacted", "appointment_set", "sold", "closed_lost"];
const LABELS = { new: "New", contacted: "Contacted", appointment_set: "Appointment Set", sold: "Sold", closed_lost: "Closed/Lost" };
const COLORS = { new: "bg-mtm-purple text-white", contacted: "bg-mtm-teal text-white", appointment_set: "bg-amber-500 text-white", sold: "bg-green-600 text-white", closed_lost: "bg-slate-400 text-white" };

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = useCallback(() => {
    const q = filter !== "all" ? `?status=${filter}` : "";
    api.get(`/admin/leads${q}`).then((r) => setLeads(r.data || [])).catch(() => {});
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try { await api.put(`/admin/leads/${id}`, { status }); toast.success("Lead updated"); load(); }
    catch { toast.error("Update failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try { await api.delete(`/admin/leads/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-leads">
      <AdminHeader title="Leads" subtitle="Customer inquiries from inventory and contact forms"
        action={<Select value={filter} onValueChange={setFilter}><SelectTrigger className="w-44" data-testid="leads-filter"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All Statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{LABELS[s]}</SelectItem>)}</SelectContent></Select>} />

      <div className="space-y-3">
        {leads.map((l) => (
          <div key={l.id} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`lead-${l.id}`}>
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-slate-900">{l.name}</span>
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${COLORS[l.status] || "bg-slate-200"}`}>{LABELS[l.status] || l.status}</span>
                  <span className="text-xs text-mtm-teal-dark font-mono">{l.cta_type}</span>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-slate-600">
                  {l.phone && <a href={`tel:${l.phone}`} className="flex items-center gap-1 hover:text-mtm-purple"><Phone size={13} /> {l.phone}</a>}
                  {l.email && <a href={`mailto:${l.email}`} className="flex items-center gap-1 hover:text-mtm-purple"><Mail size={13} /> {l.email}</a>}
                </div>
                {l.boat_title && <p className="text-xs text-slate-500 mt-1 font-mono">Boat: {l.boat_title} {l.stock_number && `(#${l.stock_number})`}</p>}
                {l.message && <p className="text-sm text-slate-600 mt-2 bg-slate-50 rounded-lg p-3">{l.message}</p>}
                <p className="text-[11px] text-slate-400 mt-2 font-mono">{l.originating_page} · {new Date(l.created_at).toLocaleString()}</p>
              </div>
              <div className="flex items-center gap-2">
                <Select value={l.status} onValueChange={(v) => setStatus(l.id, v)}>
                  <SelectTrigger className="w-40" data-testid={`lead-status-${l.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{LABELS[s]}</SelectItem>)}</SelectContent>
                </Select>
                <Button variant="ghost" size="icon" onClick={() => del(l.id)} className="text-slate-400 hover:text-red-600" data-testid={`lead-delete-${l.id}`}><Trash2 size={16} /></Button>
              </div>
            </div>
          </div>
        ))}
        {!leads.length && <div className="bg-white rounded-xl border border-slate-200 p-10 text-center text-slate-400">No leads yet.</div>}
      </div>
    </div>
  );
}
